import { useEffect } from 'react';
import dayjs from 'dayjs';
import { IconButton } from '../../../../button/IconButton/IconButton';
import { Icon } from '../../../../icon/Icon';
import { EntityUIState } from '../../../../request/interfaces/entity-ui-state';
import { useQuery } from '../../../../request/hooks/useQuery';
import { useConfirm } from '../../../../modal/ConfirmModal/useConfirm';
import { Banner } from '../../../../banner/Banner';
import { SourceAPI } from '../../../../sources/api/SourceAPI';

interface RefreshSchemaProps {
  refreshSchema: VoidFunction;
  submittingRefreshRequest: boolean;
  sourceId: number;
}

export function RefreshSchema({
  refreshSchema,
  sourceId,
  submittingRefreshRequest
}: RefreshSchemaProps) {
  const {
    data,
    uiState,
    fetch: fetchLastUpdatedTs
  } = useQuery({
    dataSource$: () =>
      SourceAPI.getCatalogLastRefreshedAt(sourceId).then(res => ({
        lastRefreshedTS: res.data.refreshed_at
      }))
  });

  const { confirm } = useConfirm();

  useEffect(() => {
    fetchLastUpdatedTs();
  }, []);

  const onRefreshClick = () => {
    confirm({
      title: 'Refresh Schema',
      positiveButtonText: 'Refresh',
      positiveBtnProps: {
        icon: 'refresh'
      },
      negativeButtonText: 'Cancel',
      BodyComponent: RefreshWarning
    }).then(confirmed => {
      if (confirmed) {
        refreshSchema();
      }
    });
  };

  return (
    <div className='center-flex-row'>
      <div className='center-flex-row'>
        {uiState === EntityUIState.IDLE && data?.lastRefreshedTS
          ? `Last schema refreshed on ${dayjs(data.lastRefreshedTS).format('Do MMMM YYYY hh:mm A')}`
          : null}

        {uiState === EntityUIState.IDLE && !data?.lastRefreshedTS ? 'Schema is up to date' : null}

        {uiState === EntityUIState.LOADING ? (
          <span
            data-id='loading'
            className='shimmer shimmer-line'
            style={{ width: 130, height: 10 }}
          />
        ) : null}

        {uiState === EntityUIState.ERRORED ? 'Failed to fetch last refreshed time' : null}
      </div>

      <IconButton
        dataId='refresh'
        onClick={onRefreshClick}
        label='Refresh Schema'
        className='ml-2'
        disabled={uiState === EntityUIState.LOADING || submittingRefreshRequest}
      >
        <Icon name='refresh' className='text-primary' />
      </IconButton>
    </div>
  );
}

function RefreshWarning() {
  return (
    <Banner open dataId='warning' color='warning' icon='warning'>
      <span className='text-body-1'>Any changes in the Object Configuration will be lost</span>
    </Banner>
  );
}
